import type { AttendanceStatus, MeetingStatus } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { useApi } from '../api-context.tsx'
import { useOfflineQueue, type QueuedAttendance } from '../offline/context.tsx'
import { randomUUIDv7 } from '../offline/id.ts'
import { PermanentSendError, type QueuedItem } from '../offline/queue.ts'
import { groupKeys } from './keys.ts'

/**
 * Meetings and attendance.
 *
 * Attendance is the one write in this app that must survive a bad connection,
 * so it goes through the offline queue rather than straight to the server: the
 * submission is written to the queue, the queue is flushed, and the UI reports
 * success as soon as the *queue* has it. A leader in a basement gets the same
 * confirmation as one in the office, and the server sorts out duplicates
 * because every submission carries its own id.
 */

export const meetingKeys = {
  all: (groupId: string) => [...groupKeys.detail(groupId), 'meetings'] as const,
  list: (groupId: string, window: { from?: string; to?: string } = {}) =>
    [...meetingKeys.all(groupId), 'list', window] as const,
  detail: (groupId: string, date: string) => [...meetingKeys.all(groupId), date] as const,
  report: (groupId: string, query: Record<string, unknown> = {}) =>
    [...meetingKeys.all(groupId), 'report', query] as const,
}

export function useMeetings(groupId: string, window: { from?: string; to?: string } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: meetingKeys.list(groupId, window),
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).meetings.get({
        query: window,
      })
      if (error) throw error
      return data
    },
  })
}

export type Meeting = NonNullable<ReturnType<typeof useMeetings>['data']>[number]

export function useMeeting(groupId: string, date: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: meetingKeys.detail(groupId, date),
    enabled: enabled && date !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).meetings({ date }).get()
      if (error) throw error
      return data
    },
  })
}

export type MeetingDetail = NonNullable<ReturnType<typeof useMeeting>['data']>
export type AttendanceEntry = MeetingDetail['entries'][number]

export function useAttendanceReport(
  groupId: string,
  query: { from?: string; to?: string; missingAfter?: number } = {},
  enabled = true,
) {
  const api = useApi()
  return useQuery({
    queryKey: meetingKeys.report(groupId, query),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1
        .groups({ id: groupId })
        .attendance.report.get({ query })
      if (error) throw error
      return data
    },
  })
}

function useMeetingInvalidation(groupId: string) {
  const queryClient = useQueryClient()
  return (date?: string) => {
    void queryClient.invalidateQueries({ queryKey: meetingKeys.all(groupId) })
    if (date) void queryClient.invalidateQueries({ queryKey: meetingKeys.detail(groupId, date) })
  }
}

export function usePatchMeeting(groupId: string) {
  const api = useApi()
  const invalidate = useMeetingInvalidation(groupId)
  return useMutation({
    mutationFn: async ({
      date,
      ...body
    }: {
      date: string
      status?: MeetingStatus
      title?: string | null
      location?: string | null
      note?: string | null
      startsAt?: string
      endsAt?: string
    }) => {
      const { data, error } = await api.api.v1
        .groups({ id: groupId })
        .meetings({ date })
        .patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (_data, variables) => invalidate(variables.date),
  })
}

export interface AttendanceInput {
  occurrenceDate: string
  entries: {
    clientId?: string
    personId?: string
    guestName?: string
    status: AttendanceStatus
    note?: string | null
  }[]
  headcount?: number | null
  note?: string | null
  takenAt?: string
  startsAt?: string
  endsAt?: string
}

/**
 * Sends one queued submission. A 4xx is permanent — replaying it will fail
 * identically forever — so it is thrown as such and the item is dropped rather
 * than blocking everything queued behind it.
 */
function useSender() {
  const api = useApi()
  return useCallback(
    async (item: QueuedItem<QueuedAttendance>) => {
      const { error } = await api.api.v1
        .groups({ id: item.payload.groupId })
        .meetings({ date: item.payload.occurrenceDate })
        .attendance.put(item.payload.body as never)
      if (!error) return

      const status = typeof error.status === 'number' ? error.status : 0
      if (status >= 400 && status < 500) {
        throw new PermanentSendError(`${status} rejected this submission`)
      }
      throw new Error(`Could not reach the server (${status || 'offline'})`)
    },
    [api],
  )
}

/**
 * Take attendance.
 *
 * `submissionId` is generated here rather than by the caller so that a retry
 * of the same tap-through cannot become two submissions; a *correction* passes
 * the same id and replaces the queued payload.
 */
export function useSubmitAttendance(groupId: string) {
  const queue = useOfflineQueue()
  const send = useSender()
  const invalidate = useMeetingInvalidation(groupId)

  return useMutation({
    mutationFn: async (input: AttendanceInput & { submissionId?: string }) => {
      const submissionId = input.submissionId ?? randomUUIDv7()
      const body = {
        submissionId,
        entries: input.entries,
        headcount: input.headcount ?? null,
        note: input.note ?? null,
        takenAt: input.takenAt ?? new Date().toISOString(),
        ...(input.startsAt && input.endsAt
          ? { startsAt: input.startsAt, endsAt: input.endsAt }
          : {}),
      }
      const payload: QueuedAttendance = {
        groupId,
        occurrenceDate: input.occurrenceDate,
        body,
      }

      // No queue configured (the web): post directly and let the error surface.
      if (!queue) {
        await send({ id: submissionId, payload, queuedAt: Date.now(), attempts: 0 })
        return { queued: false, submissionId }
      }

      await queue.enqueue(submissionId, payload)
      const result = await queue.flush(send)
      // Queued but unsent is a success from the leader's point of view: it is
      // written down and it will arrive. Saying otherwise would teach them to
      // tap everything twice.
      return { queued: result.remaining > 0, submissionId }
    },
    onSuccess: (_result, input) => invalidate(input.occurrenceDate),
  })
}

/**
 * Drains the queue and reports what is still waiting.
 *
 * Flushes on mount, whenever the caller says the network came back, and on a
 * slow timer as a backstop — a phone that never fires a reachability event
 * should still catch up eventually.
 */
export function useAttendanceSync({
  online = true,
  intervalMs = 60_000,
}: { online?: boolean; intervalMs?: number } = {}) {
  const queue = useOfflineQueue()
  const send = useSender()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(0)
  const [isSyncing, setSyncing] = useState(false)

  const flush = useCallback(async () => {
    if (!queue) return
    setSyncing(true)
    try {
      const result = await queue.flush(send)
      setPending(result.remaining)
      if (result.sent > 0) {
        // Something reached the server: every meeting view is now stale.
        void queryClient.invalidateQueries({ queryKey: ['groups'] })
      }
    } finally {
      setSyncing(false)
    }
  }, [queue, send, queryClient])

  useEffect(() => {
    if (!queue) return
    void queue.list().then((items) => setPending(items.length))
  }, [queue])

  useEffect(() => {
    if (!queue || !online) return
    void flush()
    const timer = setInterval(() => void flush(), intervalMs)
    return () => clearInterval(timer)
  }, [queue, online, intervalMs, flush])

  return { pending, isSyncing, flush }
}
